import { useMutation, useSuspenseQuery } from '@apollo/client';
import startCase from 'lodash/startCase';
import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { eluveAdminHasuraContext } from '@eluve/apollo-client';
import { InlineEdit } from '@eluve/blocks';
import {
  Button,
  Content,
  DebugJson,
  Header,
  Heading,
  Icon,
  Separator,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  SidebarTrigger,
  Stack,
  Tag,
  Text,
  Textarea,
  useToast,
} from '@eluve/components';
import { TemplateVariantCard } from '@eluve/frontend-feature-prompt-templates';
import { LlmOutputTypesLookup } from '@eluve/graphql-types';
import { FragmentOf } from '@eluve/graphql.tada';
import {
  convertDynamicArtifactToJsonSchema,
  dynamicArtifactTemplateSchema,
  hydrateDynamicArtifactTemplatePreview,
} from '@eluve/llm-outputs';
import {
  useIsEluveAdmin,
  useIsPromptTemplateCreator,
  useIsTemplatePublisher,
} from '@eluve/session-helpers';
import { DynamicArtifactRenderer } from '@eluve/smart-blocks';
import { formatHumanName } from '@eluve/utils';

import {
  getOutputTemplateQuery,
  setOutputTemplateAsDefaultMutation,
  templateVariantFragment,
  updateOutputTemplateMutation,
  updateVariantNotesMutation,
} from './operations';
import { useIsOrganizationRoute } from './useIsOrganizationRoute';
import { useOutputTemplateRoutePath } from './useOutputTemplateRoutePath';

export const OutputTemplateDetailsPage: React.FC = () => {
  const toast = useToast();
  const { outputTemplateId, variantNumber } = useParams() as {
    outputTemplateId: string;
    variantNumber?: string;
  };
  const [selectedVariantNumber, setSelectedVariantNumber] = useState<
    number | null
  >(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const navigate = useNavigate();

  const isEluveAdmin = useIsEluveAdmin();
  const isTemplateCreator = useIsPromptTemplateCreator();
  const isTemplatePublisher = useIsTemplatePublisher();

  const isOrganizationRoute = useIsOrganizationRoute();

  const {
    outputTemplateBasePath,
    createCloneOutputVariantPath,
    createEditVariantPath,
  } = useOutputTemplateRoutePath();

  const {
    data: { llmOutputTemplatesByPk: outputTemplateData },
  } = useSuspenseQuery(getOutputTemplateQuery, {
    variables: {
      id: outputTemplateId,
    },
  });

  const [setOutputTemplateAsDefault] = useMutation(
    setOutputTemplateAsDefaultMutation,
    {
      context: isEluveAdmin ? eluveAdminHasuraContext : undefined,
      onError: () => {
        toast.error('Variant could not be set as the default.');
      },
    },
  );

  const [updateOutputTemplate] = useMutation(updateOutputTemplateMutation, {
    context: isEluveAdmin ? eluveAdminHasuraContext : undefined,
    onError: () => {
      toast.error('Template could not be updated.');
      setIsUpdating(false);
    },
    onCompleted: () => {
      setIsUpdating(false);
    },
  });

  const [updateVariantNotes] = useMutation(updateVariantNotesMutation, {
    context: isEluveAdmin ? eluveAdminHasuraContext : undefined,
    onError: () => {
      toast.error('Variant notes could not be updated.');
    },
  });

  const selectedVariantTemplate = useMemo(() => {
    let variantTemplate: FragmentOf<typeof templateVariantFragment> | null =
      null;

    if (variantNumber) {
      variantTemplate =
        outputTemplateData?.variants.find(
          (variant) => variant.variantNumber === Number(variantNumber),
        ) ?? null;
    }

    // If variant number is not specified in the URL, fall back to the current default variant
    variantTemplate ??=
      outputTemplateData?.variants.find(
        (variant) => variant.isCurrentDefault,
      ) ?? null;

    // If there's no current default, fall back to the latest variant
    variantTemplate ??=
      outputTemplateData?.variants
        .slice()
        .sort((a, b) => b.variantNumber - a.variantNumber)[0] ?? null;

    if (!variantTemplate) {
      return undefined;
    }

    const parsed = dynamicArtifactTemplateSchema.safeParse(
      variantTemplate?.template,
    );
    setSelectedVariantNumber(variantTemplate.variantNumber);

    if (!parsed.success) {
      return undefined;
    }

    return parsed.data;
  }, [outputTemplateData?.variants, variantNumber]);

  const handleUpdateName = async (newName: string) => {
    setIsUpdating(true);
    await updateOutputTemplate({
      variables: {
        id: outputTemplateId,
        name: newName,
        description: outputTemplateData?.description,
      },
    });
  };

  const handleUpdateDescription = async (newDescription: string) => {
    setIsUpdating(true);
    await updateOutputTemplate({
      variables: {
        id: outputTemplateId,
        name: outputTemplateData?.name,
        description: newDescription,
      },
    });
  };

  const handleSetAsDefaultClick = (variantNumber: number) => {
    setOutputTemplateAsDefault({
      variables: {
        outputTemplateId,
        variantNumber,
      },
      optimisticResponse: () => ({
        updateLlmOutputTemplateVariants: {
          returning: [
            {
              __typename: 'LlmOutputTemplateVariants' as const,
              outputTemplateId,
              variantNumber,
              isCurrentDefault: false,
            },
          ],
        },
        updateLlmOutputTemplateVariantsByPk: {
          __typename: 'LlmOutputTemplateVariants' as const,
          outputTemplateId,
          variantNumber,
          isCurrentDefault: true,
        },
      }),
    });
  };

  const handleUpdateVariantNotes = async (newNotes: string) => {
    if (selectedVariantNumber === null) return;

    await updateVariantNotes({
      variables: {
        outputTemplateId,
        variantNumber: selectedVariantNumber,
        variantNotes: newNotes || null,
      },
    });
  };

  const { exportableJson, hydratedTemplate, outputTypeLabel, jsonSchema } =
    useMemo(() => {
      if (!selectedVariantTemplate) {
        return {
          exportableJson: undefined,
          hydratedTemplate: undefined,
          outputTypeLabel: undefined,
          jsonSchema: undefined,
        };
      }

      const outputType =
        outputTemplateData?.llmOutputType ?? LlmOutputTypesLookup.SOAP_NOTE;
      const exportContent = {
        type: 'hydrateArtifact',
        artifactTemplate: selectedVariantTemplate,
        outputType,
        isImported: Boolean(
          outputTemplateData?.external_chart_templates?.length,
        ),
      };

      const templateJsonSchema = convertDynamicArtifactToJsonSchema({
        name: outputTemplateData?.name ?? '',
        description: outputTemplateData?.description ?? '',
        blocks: selectedVariantTemplate.blocks,
      }).valueOf();

      return {
        exportableJson: JSON.stringify(exportContent, null, 2),
        hydratedTemplate: hydrateDynamicArtifactTemplatePreview(
          selectedVariantTemplate,
        ),
        outputTypeLabel: startCase(
          outputType.split('_').join(' ').toLowerCase(),
        ),
        jsonSchema: templateJsonSchema,
      };
    }, [
      selectedVariantTemplate,
      outputTemplateData?.llmOutputType,
      outputTemplateData?.external_chart_templates?.length,
      outputTemplateData?.name,
      outputTemplateData?.description,
    ]);

  const handleOnCopyClick = async (data: string) => {
    try {
      await navigator.clipboard.writeText(data);
      toast.success('Variant JSON copied to clipboard.');
    } catch {
      toast.error('Variant JSON could not be copied.');
    }
  };

  return (
    <>
      {isOrganizationRoute && (
        <Header>
          <SidebarTrigger />
          <div className="my-2 py-2">
            <Heading>Templates</Heading>
          </div>
        </Header>
      )}
      <Content padded>
        <Stack gap={8}>
          <Stack gap={2}>
            <InlineEdit
              value={outputTemplateData?.name ?? ''}
              onSave={handleUpdateName}
              isLoading={isUpdating}
              isReadOnly={!(isEluveAdmin || isTemplateCreator)}
              placeholder="Template name"
              className="mt-4 w-full"
            >
              <Heading size="xl" weight="semibold">
                {outputTemplateData?.name}
              </Heading>
              {outputTypeLabel && (
                <Tag
                  color="gray"
                  label={outputTypeLabel}
                  size="s"
                  subtle={false}
                />
              )}
            </InlineEdit>
            <InlineEdit
              value={outputTemplateData?.description ?? ''}
              onSave={handleUpdateDescription}
              isLoading={isUpdating}
              isReadOnly={!(isEluveAdmin || isTemplateCreator)}
              multiline
              placeholder="Template description"
              className="w-full text-sm"
            >
              <Text size="md" className="line-clamp-2">
                {outputTemplateData?.description}
              </Text>
            </InlineEdit>
          </Stack>
          <Stack gap={8} align="stretch" direction="horizontal">
            <Stack className="flex-1 sm:min-w-60 md:min-w-72" gap={2}>
              <Heading size="lg">Variants</Heading>
              {(outputTemplateData?.variants ?? []).map((variant) => (
                <TemplateVariantCard
                  isSelected={variant.variantNumber === selectedVariantNumber}
                  key={variant.variantNumber}
                  onClick={() => {
                    navigate(
                      `${outputTemplateBasePath}/${outputTemplateId}/variant/${variant.variantNumber}`,
                    );
                  }}
                  onDuplicateClick={() => {
                    navigate(
                      createCloneOutputVariantPath(variant.variantNumber),
                    );
                  }}
                  onVariantEditClick={() => {
                    navigate(createEditVariantPath(variant.variantNumber));
                  }}
                  onSetAsDefaultClick={
                    !(isEluveAdmin || isTemplatePublisher)
                      ? undefined
                      : () => {
                          handleSetAsDefaultClick(variant.variantNumber);
                        }
                  }
                  variant={{
                    ...variant,
                    createdBy: variant.creator
                      ? formatHumanName(
                          variant.creator.firstName,
                          variant.creator.lastName,
                        )
                      : undefined,
                    isDraft: variant.isDraft,
                  }}
                  viewOnly={!(isEluveAdmin || isTemplateCreator)}
                />
              ))}
            </Stack>
            <Separator orientation="vertical" />
            <Stack gap={2}>
              <Stack justify="between" gap={2} direction="horizontal">
                <Heading size="lg">Preview</Heading>
                <Stack fullWidth={false} gap={2} direction="horizontal">
                  {exportableJson && (
                    <Sheet>
                      <SheetTrigger asChild>
                        <Button variant="outline">
                          <Icon name="ClipboardPaste" size="auto" />
                          Export
                        </Button>
                      </SheetTrigger>
                      <SheetContent side="bottom">
                        <SheetHeader className="flex-row items-center justify-between gap-2 pr-16">
                          <SheetTitle>Variant JSON</SheetTitle>
                          <Button
                            onClick={() => handleOnCopyClick(exportableJson)}
                          >
                            <Icon name="Copy" size="auto" />
                            Copy to clipboard
                          </Button>
                        </SheetHeader>
                        <div className="container flex h-[75vh] flex-col pb-4">
                          <Textarea
                            value={exportableJson}
                            className="w-full grow border font-mono"
                            readOnly
                          />
                        </div>
                      </SheetContent>
                    </Sheet>
                  )}
                  {jsonSchema && (
                    <Sheet>
                      <SheetTrigger asChild>
                        <Button variant="outline">
                          <Icon name="Braces" size="auto" />
                          View JSON Schema
                        </Button>
                      </SheetTrigger>
                      <SheetContent side="bottom">
                        <SheetHeader className="flex-row items-center justify-between gap-2 pr-16">
                          <SheetTitle>Output Template JSON Schema</SheetTitle>
                          <Button
                            onClick={async () => {
                              try {
                                await navigator.clipboard.writeText(
                                  JSON.stringify(jsonSchema),
                                );
                                toast.success(
                                  'JSON Schema copied to clipboard.',
                                );
                              } catch {
                                toast.error('JSON Schema could not be copied.');
                              }
                            }}
                          >
                            <Icon name="Copy" size="auto" />
                            Copy to clipboard
                          </Button>
                        </SheetHeader>
                        <div className="container h-[75vh] overflow-auto pb-4">
                          <DebugJson value={jsonSchema} />
                        </div>
                      </SheetContent>
                    </Sheet>
                  )}
                </Stack>
              </Stack>
              <DynamicArtifactRenderer
                blocks={hydratedTemplate?.blocks ?? []}
                isReadonly
              />
              <>
                <Separator />
                <Stack gap={2}>
                  <Heading size="lg">Variant Notes</Heading>
                  <InlineEdit
                    value={
                      outputTemplateData?.variants.find(
                        (variant) =>
                          variant.variantNumber === selectedVariantNumber,
                      )?.variantNotes ?? ''
                    }
                    onSave={handleUpdateVariantNotes}
                    isReadOnly={!(isEluveAdmin || isTemplateCreator)}
                    placeholder="No notes."
                    multiline
                    className="w-full"
                  >
                    <Text
                      size="xs"
                      color="secondary"
                      className="whitespace-pre-wrap italic"
                    >
                      {outputTemplateData?.variants.find(
                        (variant) =>
                          variant.variantNumber === selectedVariantNumber,
                      )?.variantNotes || 'No notes.'}
                    </Text>
                  </InlineEdit>
                </Stack>
              </>
            </Stack>
          </Stack>
        </Stack>{' '}
      </Content>
    </>
  );
};
